import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Bouton, Champ, Dialogue, IconeCroix, IconeRecherche, useRetours, ZoneTexte,
} from '@journal/ui';
import { pluriel, type Objectif, type ObjectifPropose } from './modele.ts';

/**
 * Les objectifs d'une séquence ou d'une séance.
 *
 * Même esprit que le cahier journal : une proposition du programme n'est
 * qu'un point de départ, l'enseignant la réécrit comme il l'entend. On peut
 * aussi écrire un objectif à la main, et retirer n'importe quelle ligne.
 */

/** Normalise pour un filtre insensible à la casse ET aux accents. */
const normaliser = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function BlocObjectifs({
  quoi, objectifs, surChangement, propositions,
}: {
  /** « séquence » ou « séance ». */
  quoi: string;
  objectifs: Objectif[];
  surChangement: (liste: Objectif[]) => void;
  /**
   * Ce que le programme propose, au moment du clic : le domaine d'une séquence
   * peut avoir changé depuis l'ouverture de la fenêtre. Une chaîne explique
   * pourquoi il n'y a rien à proposer.
   */
  propositions: () => { intitule: string; liste: ObjectifPropose[] } | string;
}) {
  const { notifier } = useRetours();
  const [libre, setLibre] = useState('');
  const [choix, setChoix] = useState<{ intitule: string; liste: ObjectifPropose[] } | null>(null);

  function ajouterLibre() {
    const texte = libre.trim();
    if (!texte) return;
    surChangement([...objectifs, { text: texte, curriculumObjectiveId: null }]);
    setLibre('');
  }

  function ouvrirProgramme() {
    const r = propositions();
    if (typeof r === 'string') { notifier(r, 'info'); return; }
    setChoix(r);
  }

  function prendre(o: ObjectifPropose) {
    // Une ligne reprise deux fois n'apporte rien : on prévient plutôt que de doubler.
    if (objectifs.some((x) => x.curriculumObjectiveId === o.id)) {
      notifier('Cet objectif est déjà dans la liste.', 'info');
      return;
    }
    surChangement([...objectifs, { text: o.texte, curriculumObjectiveId: o.id }]);
    setChoix(null); // choisir, c'est avoir fini
    notifier('Objectif ajouté - vous pouvez le réécrire.');
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1.5 text-[13px] font-medium text-[var(--color-fg)]">
        {`Objectifs de la ${quoi}`}
      </legend>

      {objectifs.length === 0 ? (
        <p className="text-xs text-[var(--color-fg-muted)]">Aucun objectif pour l’instant.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {objectifs.map((o, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <div className="min-w-0 flex-1">
                <LigneObjectif
                  numero={i + 1}
                  valeur={o.text}
                  surSaisie={(texte) => surChangement(
                    objectifs.map((x, k) => (k === i ? { ...x, text: texte } : x)),
                  )}
                />
              </div>
              <Bouton
                variante="discret"
                taille="sm"
                iconeSeule
                icone={<IconeCroix taille={14} />}
                aria-label={`Retirer l’objectif ${i + 1}`}
                onClick={() => surChangement(objectifs.filter((_, k) => k !== i))}
                className="mt-1"
              >
                Retirer
              </Bouton>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-48 flex-1">
          <Champ
            libelle="Nouvel objectif"
            libelleMasque
            placeholder="Écrire un objectif…"
            value={libre}
            onChange={(e) => setLibre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); ajouterLibre(); }
            }}
          />
        </div>
        <Bouton variante="secondary" onClick={ajouterLibre}>Ajouter</Bouton>
        <Bouton variante="ghost" onClick={ouvrirProgramme}>Depuis le programme</Bouton>
      </div>

      {choix && (
        <FenetreProgramme
          intitule={choix.intitule}
          liste={choix.liste}
          surChoix={prendre}
          surFermeture={() => setChoix(null)}
        />
      )}
    </fieldset>
  );
}

/** Une ligne d'objectif : elle grandit avec son texte, sans ascenseur. */
function LigneObjectif({
  numero, valeur, surSaisie,
}: {
  numero: number;
  valeur: string;
  surSaisie: (texte: string) => void;
}) {
  const zone = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = zone.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [valeur]);
  return (
    <ZoneTexte
      ref={zone}
      libelle={`Objectif ${numero}`}
      libelleMasque
      rows={1}
      value={valeur}
      onChange={(e) => surSaisie(e.target.value)}
      className="resize-none overflow-hidden py-1.5"
    />
  );
}

/**
 * Les objectifs du programme, dans une fenêtre posée par-dessus la saisie.
 *
 * Une vraie fenêtre, comme dans la v1 : on choisit, elle se referme, et l'on
 * revient où l'on en était. Une liste dépliée sous le champ obligeait à faire
 * défiler pour retrouver sa saisie. Échap ne ferme qu'ELLE : la fenêtre de
 * dessous reste ouverte — c'est le comportement des fenêtres empilées.
 */
function FenetreProgramme({
  intitule, liste, surChoix, surFermeture,
}: {
  intitule: string;
  liste: ObjectifPropose[];
  surChoix: (o: ObjectifPropose) => void;
  surFermeture: () => void;
}) {
  const [filtre, setFiltre] = useState('');
  const groupes = useMemo(() => {
    const cible = normaliser(filtre.trim());
    const parGroupe = new Map<string, ObjectifPropose[]>();
    for (const o of liste) {
      if (cible && !normaliser(o.texte).includes(cible)) continue;
      parGroupe.set(o.groupe, [...(parGroupe.get(o.groupe) ?? []), o]);
    }
    return [...parGroupe.entries()];
  }, [filtre, liste]);

  return (
    <Dialogue
      ouvert
      titre="Objectifs du programme"
      description={`${intitule} · ${pluriel(liste.length, 'objectif')}`}
      surFermeture={surFermeture}
      largeur="lg"
      fermetureLibre
    >
      <div className="flex min-h-0 flex-col gap-3">
        <Champ
          libelle="Filtrer les objectifs"
          libelleMasque
          icone={<IconeRecherche taille={15} />}
          placeholder="Filtrer…"
          value={filtre}
          onChange={(e) => setFiltre(e.target.value)}
          autoFocus
        />
        <div className="flex flex-col gap-4">
          {groupes.length === 0 && (
            <p className="text-sm text-[var(--color-fg-muted)]">Aucun objectif ne correspond.</p>
          )}
          {groupes.map(([groupe, objectifs]) => (
            <section key={groupe} className="flex flex-col gap-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                {groupe}
              </h3>
              {objectifs.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => surChoix(o)}
                  className={[
                    'rounded-lg border border-[var(--color-border)] px-3 py-2 text-left text-sm',
                    'text-[var(--color-fg)] hover:border-[var(--color-primary)]',
                    'hover:bg-[var(--color-primary-soft)]',
                    'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
                  ].join(' ')}
                >
                  {o.texte}
                </button>
              ))}
            </section>
          ))}
        </div>
      </div>
    </Dialogue>
  );
}
